import type { Action, Thunk, Computed } from 'easy-peasy';
import { action, thunk, computed } from 'easy-peasy';
import type { StoreModel } from '..';
import type {
  UserCreationRequest,
  UserFetchParams,
  UserSsoFetchParams,
  UserSsoMutate,
} from '../services/PartnerService/types';
import type { User } from '../services/AuthService/types';
import type { Injections } from '../services/injections';
import type { ApplicationErrorOptions } from '../../types';
import { cookies } from '../session';
import parseStatusCode from '../errors/parseStatusCode';
import config from '../../config';
import { isShipiumEmail } from '../../utils/regex';
import { SKIPPED_FETCH } from '../data-fetching';
import { mapUserToState } from './utils';
const { ACCESS_TOKEN_NAME } = config;

// Listing partner users requires admin authorities (PARTNER_ADMIN / SSO_USER_ADMIN),
// which only partner administrators and Shipium users hold; everyone else gets a 403.
const canListPartnerUsers = (sessionUser?: User) =>
  sessionUser?.sessionPartnerRole === 'administrator' || isShipiumEmail(sessionUser?.email);

export interface UserModel {
  users: Record<string, User>;
  usersInvited: Computed<UserModel, boolean>;
  selectedEditUser: User | null;
  setEditUser: Action<UserModel, User | null>;
  lastAction?: number;
  userCollection: Computed<UserModel, User[]>;
  userActionStart: Action<UserModel, void>;
  fetchUsers: Thunk<UserModel, string | undefined, Injections, StoreModel, Promise<User[]>>;
  fetchAllUsers: Thunk<UserModel, UserFetchParams | UserSsoFetchParams, Injections, StoreModel, Promise<User[]>>;
  setUsers: Action<UserModel, User[]>;
  setUser: Action<UserModel, User>;
  fetchUsersFailure: Action<UserModel, ApplicationErrorOptions>;
  addUser: Thunk<UserModel, UserCreationRequest, Injections, StoreModel, Promise<User>>;
  removeUser: Action<UserModel, string>;
  addUserFailure: Action<UserModel, ApplicationErrorOptions>;
  updateUserFailure: Action<UserModel, ApplicationErrorOptions>;
  removeUserFailure: Action<UserModel, ApplicationErrorOptions>;
  fetchSsoUsers: Thunk<UserModel, undefined, Injections, StoreModel, Promise<User[]>>;
  fetchAllSsoUsers: Thunk<UserModel, undefined, Injections, StoreModel, Promise<User[]>>;
  addSsoUser: Thunk<UserModel, UserSsoMutate, Injections, StoreModel, Promise<User>>;
  updateSsoUser: Thunk<
    UserModel,
    Partial<UserSsoMutate> & { userId: string },
    Injections,
    StoreModel,
    Promise<User | void>
  >;
  removeSsoUser: Thunk<UserModel, User, Injections, StoreModel, Promise<void>>;
}

export const userModel: UserModel = {
  users: {},
  selectedEditUser: null,
  userCollection: computed((state) => {
    return Object.values(state.users) || [];
  }),
  userActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  updateSsoUser: thunk(async (actions, userUpdateRequest, { injections, getStoreState }) => {
    try {
      const { partnerService } = injections;
      actions.userActionStart();
      const partnerId = getStoreState().session.user?.sessionPartnerId || '';
      const { userId, ...user } = userUpdateRequest;
      const response = await partnerService.updateSsoUser(userId, user);
      const newUser = mapUserToState(response, partnerId);
      actions.setUser(newUser);
      return response;
    } catch (error) {
      actions.updateUserFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to update User.',
      });
      return error;
    }
  }),
  addUser: thunk(async (actions, userCreationRequest, { injections }) => {
    try {
      const { partnerService } = injections;
      actions.userActionStart();
      const sessionAccessToken = cookies.get(ACCESS_TOKEN_NAME);
      const response = await partnerService.createPartnerUser(userCreationRequest, sessionAccessToken);
      actions.setUser(response);
      return response;
    } catch (error) {
      actions.addUserFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to add User.',
      });
      return error;
    }
  }),
  addSsoUser: thunk(async (actions, userCreationRequest, { injections, getStoreState }) => {
    try {
      const { partnerService } = injections;
      actions.userActionStart();
      const partnerId = getStoreState().session.user?.sessionPartnerId || '';
      const response = await partnerService.addSsoUser(userCreationRequest);
      const newUser = mapUserToState(response, partnerId);
      actions.setUser(newUser);
      return response;
    } catch (error) {
      actions.addUserFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to add User.',
      });
      return error;
    }
  }),
  removeSsoUser: thunk(async (actions, removalUser, { injections, getStoreState }) => {
    try {
      const { partnerService } = injections;
      const { userId } = removalUser;
      await partnerService.deleteSsoUser(userId);
      actions.removeUser(userId);
    } catch (error) {
      actions.removeUserFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to remove user',
      });
      return error;
    }
  }),
  fetchUsers: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { partnerService } = injections;
      if (!canListPartnerUsers(getStoreState().session?.user)) {
        return SKIPPED_FETCH;
      }
      const partnerId = payload || getStoreState().session.user?.sessionPartnerId || 'NA';
      actions.userActionStart();
      const response = await partnerService.getPartnerUsers(partnerId);
      actions.setUsers(response.data);
      return response;
    } catch (error) {
      actions.fetchUsersFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to fetch  Users.',
        asNotification: false,
      });
      return error;
    }
  }),
  fetchAllUsers: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { partnerService } = injections;
      if (!canListPartnerUsers(getStoreState().session?.user)) {
        return SKIPPED_FETCH;
      }
      const params = payload;
      const partnerId = getStoreState().session.user?.sessionPartnerId || 'NA';
      actions.userActionStart();
      const response = await partnerService.getPartnerUsers(partnerId, params);
      actions.setUsers(response.data);
      return response;
    } catch (error) {
      actions.fetchUsersFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to fetch  Users.',
        asNotification: false,
      });
      return error;
    }
  }),
  fetchAllSsoUsers: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { partnerService } = injections;
      if (!canListPartnerUsers(getStoreState().session?.user)) {
        return SKIPPED_FETCH;
      }
      const partnerId = payload || getStoreState().session.user?.sessionPartnerId || 'NA';
      actions.userActionStart();
      const response = await partnerService.getPartnerSsoUsers(partnerId, {
        includeInactive: true,
      });
      actions.setUsers(response);
      return response;
    } catch (error) {
      actions.fetchUsersFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to fetch  Users.',
        asNotification: false,
      });
      return error;
    }
  }),
  fetchSsoUsers: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { partnerService } = injections;
      if (!canListPartnerUsers(getStoreState().session?.user)) {
        return SKIPPED_FETCH;
      }
      const partnerId = payload || getStoreState().session.user?.sessionPartnerId || 'NA';
      actions.userActionStart();
      const response = await partnerService.getPartnerSsoUsers(partnerId);
      actions.setUsers(response);
      return response;
    } catch (error) {
      actions.fetchUsersFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to fetch  Users.',
        asNotification: false,
      });
      return error;
    }
  }),
  setEditUser: action((state, editUser) => {
    state.selectedEditUser = editUser;
  }),
  removeUser: action((state, userId) => {
    delete state.users[userId];
  }),
  setUsers: action((state, users) => {
    const userMap = users.reduce<Record<string, User>>((acc, curr) => {
      acc[curr.userId] = curr;
      return acc;
    }, {});
    state.users = userMap;
  }),
  setUser: action((state, user) => {
    state.users[user.userId] = user;
  }),
  fetchUsersFailure: action((state, error) => {
    console.error(error);
  }),
  addUserFailure: action((state, error) => {
    console.error(error);
  }),
  updateUserFailure: action((state, error) => {
    console.error(error);
  }),
  removeUserFailure: action((state, error) => {
    console.log(error);
  }),
  usersInvited: computed((state) => {
    return state.userCollection.length > 1;
  }),
};
